import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtMoney, fmtDate, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { toast } from "sonner";
import {
  Plus, Search, Pencil, Trash2, FileSignature, AlertTriangle, Clock4,
  CheckCircle2, XCircle, Download, X,
} from "lucide-react";

const CONTRACT_TYPES = [
  { v: "probation", l: "Thử việc 2 tháng", defaultDays: 60 },
  { v: "fixed_1y", l: "Chính thức 1 năm", defaultDays: 365 },
  { v: "fixed_2y", l: "Chính thức 2 năm", defaultDays: 730 },
  { v: "indefinite", l: "Vô thời hạn", defaultDays: null },
  { v: "seasonal", l: "Thời vụ 3 tháng", defaultDays: 90 },
  { v: "intern", l: "Thực tập", defaultDays: 90 },
];
const CT_LABEL = Object.fromEntries(CONTRACT_TYPES.map((t) => [t.v, t.l]));

const STATUS_LABELS = {
  active: { l: "Đang hiệu lực", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  draft: { l: "Nháp", cls: "bg-stone-50 text-stone-700 border-stone-200" },
  expired: { l: "Hết hạn", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  terminated: { l: "Đã kết thúc", cls: "bg-red-50 text-red-800 border-red-200" },
  superseded: { l: "Đã thay thế", cls: "bg-violet-50 text-violet-800 border-violet-200" },
};

const PAY_CYCLES = [
  { v: "monthly_10", l: "Ngày 10 hàng tháng" },
  { v: "monthly_15", l: "Ngày 15 hàng tháng" },
  { v: "monthly_end", l: "Cuối tháng" },
];

const BHXH_POLICIES = [
  { v: "immediate", l: "Ngay từ tháng đầu" },
  { v: "after_official", l: "Sau khi ký HĐ chính thức" },
  { v: "none", l: "Không áp dụng (thời vụ)" },
];

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / 86400000);
};

const addDays = (dateStr, days) => {
  if (!dateStr || days == null) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const emptyContract = {
  code: "",
  contract_type: "probation",
  signed_date: "",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  base_salary: 0,
  allowance: 0,
  pay_cycle: "monthly_15",
  bhxh_policy: "after_official",
  file_url: "",
  status: "active",
  note: "",
};

export default function Contracts() {
  const [data, setData] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ active_count: 0, probation_count: 0, expiring_30d_count: 0, terminated_count: 0 });

  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [view, setView] = useState("all"); // all | expiring

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [empPickOpen, setEmpPickOpen] = useState(false);
  const [form, setForm] = useState(emptyContract);
  const [editId, setEditId] = useState(null);
  const [editVersion, setEditVersion] = useState(null);
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const [empQuery, setEmpQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.contract_type = typeFilter;
      if (view === "expiring") params.expiring_days = 30;
      if (q.trim()) params.q = q.trim();
      const [list, st] = await Promise.all([
        api.get("/contracts", { params }),
        api.get("/contracts/stats").catch(() => ({ data: stats })),
      ]);
      setData(list.data);
      setStats(st.data);
    } catch (e) {
      setError(parseApiError(e, "Không tải được danh sách hợp đồng").message);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, view, q]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    api
      .get("/employees", { params: { limit: 500 } })
      .then((r) => setEmployees(r.data))
      .catch(() => {});
  }, []);

  const expiringSoon = useMemo(() => {
    return data
      .filter((c) => c.status === "active" && c.end_date)
      .map((c) => ({ ...c, _days: daysUntil(c.end_date) }))
      .filter((c) => c._days != null && c._days <= 30)
      .sort((a, b) => a._days - b._days);
  }, [data]);

  const openCreate = () => {
    setSelectedEmpId(null);
    setEmpQuery("");
    setEmpPickOpen(true);
  };

  const proceedCreate = (empId) => {
    setSelectedEmpId(empId);
    setEmpPickOpen(false);
    setForm(emptyContract);
    setEditId(null);
    setEditVersion(null);
    setOpen(true);
  };

  const openEdit = (c) => {
    setSelectedEmpId(c.employee_id);
    setForm({
      ...emptyContract,
      ...c,
      signed_date: c.signed_date?.slice(0, 10) || "",
      start_date: c.start_date?.slice(0, 10) || "",
      end_date: c.end_date?.slice(0, 10) || "",
    });
    setEditId(c.id);
    setEditVersion(c.version ?? null);
    setOpen(true);
  };

  const updateField = (k, v) =>
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "contract_type") {
        const def = CONTRACT_TYPES.find((t) => t.v === v);
        if (def && def.defaultDays && next.start_date) {
          next.end_date = addDays(next.start_date, def.defaultDays);
        }
        if (def && def.defaultDays == null) next.end_date = "";
      }
      if (k === "start_date") {
        const def = CONTRACT_TYPES.find((t) => t.v === next.contract_type);
        if (def && def.defaultDays && v) next.end_date = addDays(v, def.defaultDays);
      }
      return next;
    });

  const save = async () => {
    if (!editId && !selectedEmpId) {
      toast.error("Cần chọn nhân viên");
      return;
    }
    if (!form.start_date) {
      toast.error("Ngày bắt đầu là bắt buộc");
      return;
    }
    if (Number(form.base_salary) < 0 || Number(form.allowance) < 0) {
      toast.error("Lương và phụ cấp phải >= 0");
      return;
    }
    if (form.end_date && form.start_date && form.end_date < form.start_date) {
      toast.error("Ngày hết hạn phải sau ngày bắt đầu");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_salary: Number(form.base_salary || 0),
        allowance: Number(form.allowance || 0),
        end_date: form.end_date || null,
        signed_date: form.signed_date || null,
        code: form.code || null,
        file_url: form.file_url || null,
        note: form.note || null,
        expected_version: editId ? editVersion : null,
      };
      if (editId) {
        await api.put(`/contracts/${editId}`, payload);
        toast.success("Đã cập nhật hợp đồng");
      } else {
        await api.post(`/employees/${selectedEmpId}/contracts`, payload);
        toast.success("Đã tạo hợp đồng mới");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Lưu thất bại").message);
    } finally {
      setSaving(false);
    }
  };

  const terminate = async (id) => {
    const reason = window.prompt("Lý do kết thúc HĐ (tuỳ chọn):", "");
    if (reason === null) return;
    try {
      await api.post(`/contracts/${id}/terminate`, { terminated_reason: reason });
      toast.success("Đã kết thúc hợp đồng");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Không kết thúc được").message);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/contracts/${id}`);
      toast.success("Đã xóa");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Xóa thất bại").message);
    }
  };

  const filteredEmployees = useMemo(() => {
    const k = empQuery.trim().toLowerCase();
    if (!k) return employees.slice(0, 50);
    return employees
      .filter(
        (e) =>
          e.full_name?.toLowerCase().includes(k) ||
          e.code?.toLowerCase().includes(k) ||
          e.email?.toLowerCase().includes(k),
      )
      .slice(0, 50);
  }, [employees, empQuery]);

  const selectedEmployee = employees.find((e) => e.id === selectedEmpId);

  return (
    <div data-testid="page-contracts">
      <PageHeader
        kicker="Tổ chức"
        title="Hợp đồng lao động"
        breadcrumb={["Tổ chức", "Hợp đồng"]}
        actions={
          <Button onClick={openCreate} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-create-contract">
            <Plus className="w-4 h-4 mr-1.5" /> Tạo hợp đồng
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        {/* Expiring alert */}
        {expiringSoon.length > 0 && view !== "expiring" && (
          <div className="bg-amber-50 border border-amber-300 rounded-sm p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <div className="font-bold text-amber-900">
                {expiringSoon.length} hợp đồng sắp hết hạn trong 30 ngày
              </div>
              <div className="text-amber-800 text-xs mt-0.5">
                {expiringSoon.slice(0, 3).map((c, i) => (
                  <span key={c.id}>
                    {i > 0 && " · "}
                    <strong>{c.employee_name}</strong> ({c._days <= 0 ? "đã quá hạn" : `${c._days} ngày`})
                  </span>
                ))}
                {expiringSoon.length > 3 && ` · +${expiringSoon.length - 3} khác`}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => setView("expiring")} className="rounded-sm">
              Xem danh sách
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard
            icon={<CheckCircle2 className="w-4 h-4" />}
            label="HĐ đang hiệu lực"
            value={stats.active_count || 0}
            sub="Chính thức + Thử việc"
            tone="emerald"
          />
          <StatCard
            icon={<Clock4 className="w-4 h-4" />}
            label="Sắp hết hạn 30 ngày"
            value={stats.expiring_30d_count || 0}
            sub="Cần gia hạn"
            tone="amber"
          />
          <StatCard
            icon={<FileSignature className="w-4 h-4" />}
            label="Thử việc đang chạy"
            value={stats.probation_count || 0}
            sub="Cần xét chuyển HĐ"
            tone="sky"
          />
          <StatCard
            icon={<XCircle className="w-4 h-4" />}
            label="Đã kết thúc / hết hạn"
            value={stats.terminated_count || 0}
            sub="Tổng cộng"
            tone="stone"
          />
        </div>

        {/* View tabs */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView("all")}
            className={`px-3 py-1.5 text-xs font-bold rounded-sm border ${
              view === "all"
                ? "bg-emerald-900 text-white border-emerald-900"
                : "bg-white text-stone-700 border-stone-200"
            }`}
          >
            Tất cả
          </button>
          <button
            onClick={() => setView("expiring")}
            className={`px-3 py-1.5 text-xs font-bold rounded-sm border ${
              view === "expiring"
                ? "bg-amber-600 text-white border-amber-600"
                : "bg-white text-stone-700 border-stone-200"
            }`}
            data-testid="view-expiring"
          >
            ⚠ Sắp hết hạn (30 ngày)
          </button>
        </div>

        <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
          {/* Toolbar */}
          <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[220px] max-w-sm">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Tìm theo tên NV, mã NV, mã HĐ..."
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-8 h-9 rounded-sm"
                data-testid="contracts-search"
              />
            </div>
            <Select value={statusFilter || "_all"} onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm w-[170px] text-xs">
                <SelectValue placeholder="Trạng thái" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Mọi trạng thái</SelectItem>
                {Object.entries(STATUS_LABELS).map(([v, def]) => (
                  <SelectItem key={v} value={v}>
                    {def.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter || "_all"} onValueChange={(v) => setTypeFilter(v === "_all" ? "" : v)}>
              <SelectTrigger className="h-9 rounded-sm w-[180px] text-xs">
                <SelectValue placeholder="Loại HĐ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Mọi loại</SelectItem>
                {CONTRACT_TYPES.map((t) => (
                  <SelectItem key={t.v} value={t.v}>
                    {t.l}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(statusFilter || typeFilter || q) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStatusFilter("");
                  setTypeFilter("");
                  setQ("");
                }}
                className="rounded-sm h-9 text-xs"
              >
                <X className="w-3.5 h-3.5 mr-1" /> Xóa lọc
              </Button>
            )}
            <div className="ml-auto text-xs text-stone-500 font-mono">{data.length} hợp đồng</div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  {["Nhân viên", "Mã HĐ", "Loại HĐ", "Bắt đầu", "Hết hạn", "Còn lại", "Lương", "Trạng thái", ""].map(
                    (h, i) => (
                      <th
                        key={i}
                        className="text-left px-4 py-2.5 text-[10px] uppercase tracking-wider text-stone-600 font-bold"
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {!loading &&
                  !error &&
                  data.map((c) => {
                    const days = c.end_date ? daysUntil(c.end_date) : null;
                    const warn = c.status === "active" && days != null && days <= 30;
                    const overdue = c.status === "active" && days != null && days < 0;
                    return (
                      <tr key={c.id} className={`hover:bg-stone-50 ${warn ? "bg-amber-50/40" : ""}`}>
                        <td className="px-4 py-2.5">
                          <div className="font-semibold text-stone-900">{c.employee_name}</div>
                          <div className="text-[11px] text-stone-500 font-mono">{c.employee_code}</div>
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-stone-700">{c.code || "—"}</td>
                        <td className="px-4 py-2.5">
                          <Badge variant="secondary" className="rounded-sm font-bold text-[10px] uppercase tracking-wider">
                            {CT_LABEL[c.contract_type] || c.contract_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-2.5 text-xs text-stone-700">
                          {c.start_date ? fmtDate(c.start_date) : "—"}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-stone-700">
                          {c.end_date ? fmtDate(c.end_date) : <span className="text-stone-400">Vô thời hạn</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          {days == null ? (
                            <span className="text-stone-400 text-xs">—</span>
                          ) : overdue ? (
                            <span className="text-red-700 text-xs font-bold">Quá {-days} ngày</span>
                          ) : (
                            <span
                              className={`text-xs font-bold ${
                                days <= 30 ? "text-amber-700" : "text-stone-600"
                              }`}
                            >
                              {days} ngày
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs text-stone-800 font-semibold">
                          {fmtMoney(c.base_salary)}
                          {c.allowance > 0 && (
                            <div className="text-[10px] text-stone-500">+ {fmtMoney(c.allowance)} phụ cấp</div>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <StatusBadge status={c.status} />
                        </td>
                        <td className="px-4 py-2.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => openEdit(c)}
                            className="text-stone-600 hover:text-emerald-800 mr-2"
                            title="Sửa"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          {c.status === "active" && (
                            <button
                              onClick={() => terminate(c.id)}
                              className="text-stone-600 hover:text-amber-700 mr-2"
                              title="Kết thúc HĐ"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          <DestructiveConfirmDialog
                            title="Xóa hợp đồng?"
                            description={`Hợp đồng ${c.code || ""} của ${c.employee_name} sẽ bị xóa.`}
                            confirmLabel="Xóa"
                            token={String(c.code || c.id)}
                            onConfirm={() => remove(c.id)}
                            trigger={
                              <button className="text-stone-600 hover:text-red-700" title="Xóa">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
                {loading && <DataStateRow colSpan={9} type="loading" message="Đang tải hợp đồng..." />}
                {!loading && error && <DataStateRow colSpan={9} type="error" message={error} onRetry={load} />}
                {!loading && !error && data.length === 0 && (
                  <DataStateRow colSpan={9} type="empty" message="Không có hợp đồng nào" />
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Employee picker dialog */}
      <Dialog open={empPickOpen} onOpenChange={setEmpPickOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Chọn nhân viên</DialogTitle>
          </DialogHeader>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input
              autoFocus
              placeholder="Tìm tên, mã NV..."
              value={empQuery}
              onChange={(e) => setEmpQuery(e.target.value)}
              className="pl-8 h-9 rounded-sm"
            />
          </div>
          <div className="max-h-[400px] overflow-y-auto border border-stone-200 rounded-sm divide-y divide-stone-100">
            {filteredEmployees.length === 0 && (
              <div className="text-center text-stone-500 text-sm py-8">Không có kết quả</div>
            )}
            {filteredEmployees.map((e) => (
              <button
                key={e.id}
                onClick={() => proceedCreate(e.id)}
                className="w-full px-3 py-2 hover:bg-stone-50 flex items-center justify-between text-left"
              >
                <div>
                  <div className="font-semibold text-sm text-stone-900">{e.full_name}</div>
                  <div className="text-[11px] text-stone-500 font-mono">{e.code}</div>
                </div>
                <div className="text-xs text-stone-500">{e.position || ""}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editId ? "Sửa hợp đồng" : `Tạo hợp đồng cho ${selectedEmployee?.full_name || ""}`}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Mã HĐ (tuỳ chọn)</Label>
              <Input
                value={form.code || ""}
                onChange={(e) => updateField("code", e.target.value)}
                placeholder="VD: HD-2026-0042"
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Loại hợp đồng *</Label>
              <Select value={form.contract_type} onValueChange={(v) => updateField("contract_type", v)}>
                <SelectTrigger className="h-9 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACT_TYPES.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      {t.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ngày ký</Label>
              <Input
                type="date"
                value={form.signed_date}
                onChange={(e) => updateField("signed_date", e.target.value)}
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Ngày bắt đầu *</Label>
              <Input
                type="date"
                value={form.start_date}
                onChange={(e) => updateField("start_date", e.target.value)}
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Ngày hết hạn {form.contract_type === "indefinite" && "(để trống nếu vô thời hạn)"}</Label>
              <Input
                type="date"
                value={form.end_date}
                onChange={(e) => updateField("end_date", e.target.value)}
                className="h-9 rounded-sm mt-1"
                disabled={form.contract_type === "indefinite"}
              />
            </div>
            <div>
              <Label className="text-xs">Trạng thái</Label>
              <Select value={form.status} onValueChange={(v) => updateField("status", v)}>
                <SelectTrigger className="h-9 rounded-sm mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(STATUS_LABELS).map(([v, def]) => (
                    <SelectItem key={v} value={v}>
                      {def.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Lương cơ bản (VNĐ) *</Label>
              <Input
                type="number"
                value={form.base_salary}
                onChange={(e) => updateField("base_salary", e.target.value)}
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Phụ cấp (VNĐ)</Label>
              <Input
                type="number"
                value={form.allowance}
                onChange={(e) => updateField("allowance", e.target.value)}
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Hình thức trả lương</Label>
              <Select value={form.pay_cycle || undefined} onValueChange={(v) => updateField("pay_cycle", v)}>
                <SelectTrigger className="h-9 rounded-sm mt-1">
                  <SelectValue placeholder="Chọn" />
                </SelectTrigger>
                <SelectContent>
                  {PAY_CYCLES.map((p) => (
                    <SelectItem key={p.v} value={p.v}>
                      {p.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">BHXH</Label>
              <Select value={form.bhxh_policy || undefined} onValueChange={(v) => updateField("bhxh_policy", v)}>
                <SelectTrigger className="h-9 rounded-sm mt-1">
                  <SelectValue placeholder="Chọn" />
                </SelectTrigger>
                <SelectContent>
                  {BHXH_POLICIES.map((p) => (
                    <SelectItem key={p.v} value={p.v}>
                      {p.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label className="text-xs">URL file HĐ (đã upload lên storage)</Label>
              <Input
                value={form.file_url || ""}
                onChange={(e) => updateField("file_url", e.target.value)}
                placeholder="https://..."
                className="h-9 rounded-sm mt-1"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Ghi chú</Label>
              <Textarea
                value={form.note || ""}
                onChange={(e) => updateField("note", e.target.value)}
                rows={2}
                className="rounded-sm mt-1"
              />
            </div>
          </div>

          {Number(form.base_salary) > 0 && (
            <div className="bg-stone-50 border border-stone-200 rounded-sm p-3 text-xs flex items-center gap-3 flex-wrap">
              <span className="text-stone-600">
                Tổng thu nhập tháng:{" "}
                <strong className="text-stone-900 font-mono">
                  {fmtMoney(Number(form.base_salary) + Number(form.allowance || 0))}
                </strong>
              </span>
              {form.end_date && form.start_date && (
                <span className="text-stone-600">
                  Thời hạn: <strong className="text-stone-900">
                    {Math.round((new Date(form.end_date) - new Date(form.start_date)) / 86400000)} ngày
                  </strong>
                </span>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">
              Hủy
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
              data-testid="btn-save-contract"
            >
              {saving ? "Đang lưu..." : editId ? "Cập nhật" : "Tạo hợp đồng"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone }) {
  const toneClass =
    tone === "emerald"
      ? "border-l-emerald-600"
      : tone === "sky"
      ? "border-l-sky-600"
      : tone === "amber"
      ? "border-l-amber-500"
      : "border-l-stone-400";
  const iconBg =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700"
      : "bg-stone-100 text-stone-700";
  return (
    <div className={`bg-white border border-stone-200 border-l-4 ${toneClass} rounded-sm px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">{label}</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iconBg}`}>{icon}</div>
      </div>
      <div className="font-serif text-3xl font-bold text-stone-900 mt-1 tracking-tight">{value}</div>
      <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const def = STATUS_LABELS[status] || STATUS_LABELS.draft;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border ${def.cls}`}
    >
      {def.l}
    </span>
  );
}
